import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, Chip, CircularProgress, Tooltip, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import { adminApi } from '../../api';

const EMPTY = { name: '', display_name: '', contact_name: '', contact_email: '', storage_root: '', retention_note: '', enabled: true, lcm_enabled: false };

const Facilities = () => {
  const [facilities, setFacilities] = useState([]);
  const [storageBackends, setStorageBackends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | { mode: 'create'|'edit', data: {} }
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () => Promise.all([adminApi.getFacilities(), adminApi.getStorageBackends()])
    .then(([f, b]) => { setFacilities(f); setStorageBackends(b); })
    .catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openCreate = () => setDialog({ mode: 'create', data: { ...EMPTY } });
  const openEdit = (f) => setDialog({ mode: 'edit', data: { ...f } });
  const close = () => setDialog(null);

  const save = async () => {
    setSaving(true);
    try {
      if (dialog.mode === 'create') await adminApi.createFacility(dialog.data);
      else await adminApi.updateFacility(dialog.data.id, dialog.data);
      close(); load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this facility?')) return;
    await adminApi.deleteFacility(id);
    load();
  };

  const syncFromDicom = async () => {
    setSyncing(true);
    try {
      await adminApi.syncFacilities();
      await load();
    } catch (e) { console.error(e); }
    setSyncing(false);
  };

  const field = (key, label, opts = {}) => (
    <TextField
      key={key} label={label} size="small" fullWidth
      value={dialog?.data?.[key] ?? ''}
      onChange={e => setDialog(d => ({ ...d, data: { ...d.data, [key]: e.target.value } }))}
      sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& .MuiInputLabel-root': { color: '#666' }, '& input': { color: '#e0e0e0' } }}
      {...opts}
    />
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography sx={{ color: 'primary.main', fontSize: 20, fontWeight: 700, letterSpacing: 1 }}>Facilities</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />} variant="outlined" size="small" onClick={syncFromDicom} disabled={syncing}
            sx={{ borderRadius: 0, borderColor: '#333', color: '#aaa', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}>
            Sync from DICOM
          </Button>
          <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={openCreate}
            sx={{ borderRadius: 0, borderColor: '#333', color: '#aaa', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}>
            Add Facility
          </Button>
        </Box>
      </Box>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Name', 'Display Name', 'Contact', 'Storage Backend', 'Retention', 'LCM', 'Status', ''].map(h => (
                  <TableCell key={h} sx={{ bgcolor: '#0d0d0d', color: 'primary.main', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, borderBottom: '1px solid #222' }}>
                    {h.toUpperCase()}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {facilities.map(f => (
                <TableRow key={f.id} sx={{ '&:hover': { bgcolor: '#141414' } }}>
                  <TableCell sx={{ color: '#e0e0e0', fontSize: 13, fontFamily: 'monospace', borderBottom: '1px solid #1a1a1a' }}>{f.name}</TableCell>
                  <TableCell sx={{ color: '#aaa', fontSize: 12, borderBottom: '1px solid #1a1a1a' }}>{f.display_name || '—'}</TableCell>
                  <TableCell sx={{ color: '#666', fontSize: 12, borderBottom: '1px solid #1a1a1a' }}>{f.contact_email || '—'}</TableCell>
                  <TableCell sx={{ color: '#aaa', fontSize: 12, borderBottom: '1px solid #1a1a1a' }}>
                    {f.storage_backend_name ? `${f.storage_backend_name} (${f.storage_backend_tier || 'hot'})` : (f.storage_root || 'Default')}
                  </TableCell>
                  <TableCell sx={{ color: '#666', fontSize: 12, borderBottom: '1px solid #1a1a1a' }}>{f.retention_note || '—'}</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>
                    <Chip label={f.lcm_enabled ? 'ON' : 'OFF'} size="small"
                      sx={{ fontSize: 10, height: 18, bgcolor: f.lcm_enabled ? '#003322' : '#1a1a1a', color: f.lcm_enabled ? '#66bb6a' : '#555', borderRadius: 0 }} />
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>
                    <Chip label={f.enabled ? 'ACTIVE' : 'DISABLED'} size="small"
                      sx={{ fontSize: 10, height: 18, bgcolor: f.enabled ? '#002233' : '#1a1a1a', color: f.enabled ? 'primary.main' : '#555', borderRadius: 0 }} />
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(f)} sx={{ color: '#555', '&:hover': { color: 'primary.main' } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" onClick={() => del(f.id)} sx={{ color: '#555', '&:hover': { color: '#ef9a9a' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={!!dialog} onClose={close} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#111', border: '1px solid #222', borderRadius: 1 } }}>
        <DialogTitle sx={{ color: 'primary.main', fontSize: 14, letterSpacing: 1 }}>
          {dialog?.mode === 'create' ? 'ADD FACILITY' : 'EDIT FACILITY'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {field('name', 'Institution Name (matches DICOM tag)', { disabled: dialog?.mode === 'edit' })}
            {field('display_name', 'Display Name')}
            {field('contact_name', 'Contact Name')}
            {field('contact_email', 'Contact Email')}
            {field('retention_note', 'Retention Note (e.g. 7yr HIPAA minimum)')}
            <FormControl fullWidth size="small" sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& .MuiInputLabel-root': { color: '#666' }, '& .MuiSelect-select': { color: '#e0e0e0' } }}>
              <InputLabel>Assigned Storage Backend</InputLabel>
              <Select
                label="Assigned Storage Backend"
                value={dialog?.data?.storage_backend_id || ''}
                onChange={e => setDialog(d => ({ ...d, data: { ...d.data, storage_backend_id: e.target.value || null } }))}
              >
                <MenuItem value="">Default backend</MenuItem>
                {storageBackends.filter(b => b.enabled).map(b => (
                  <MenuItem key={b.id} value={b.id}>{b.display_name || b.name} — {b.storage_tier} — {b.base_uri}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {field('storage_root', 'Legacy Storage Root Override (optional path)')}
            <FormControlLabel
              control={<Switch checked={dialog?.data?.lcm_enabled ?? false} onChange={e => setDialog(d => ({ ...d, data: { ...d.data, lcm_enabled: e.target.checked } }))} size="small" />}
              label={<Typography sx={{ color: '#aaa', fontSize: 13 }}>LCM Enabled — opt-in; disabled by default</Typography>}
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              control={<Switch checked={dialog?.data?.enabled ?? true} onChange={e => setDialog(d => ({ ...d, data: { ...d.data, enabled: e.target.checked } }))} size="small" />}
              label={<Typography sx={{ color: '#aaa', fontSize: 13 }}>Facility Active</Typography>}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} sx={{ color: '#555' }}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="outlined" sx={{ borderRadius: 0, borderColor: 'primary.main', color: 'primary.main' }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Facilities;
